"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

const API_BASE_URL = "http://localhost:3000";

type InvitationItem = {
  id: number;
  email: string;
  inviteType: string;
  accepted: boolean;
  status: string;
  createdAt: string;
  invitedByEmail?: string;
};

const INVITE_ROLES = [
  { key: "ACCOUNT_SALESPERSON", label: "Invite Account Salesperson" },
  { key: "PSE", label: "Invite PSE" },
  { key: "BSP", label: "Invite BSP" },
  { key: "EAH", label: "Invite EAH" },
];

export default function InvitationsPage() {
  const [invitations, setInvitations] = useState<InvitationItem[]>([]);
  const [activeModalRole, setActiveModalRole] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState("");
  const [sending, setSending] = useState(false);
  const [statusMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchInvitations = async () => {
    try {
      setErrorMessage(null);
      const res = await fetch(`${API_BASE_URL}/api/invitations`, {
        cache: "no-store",
      });
      if (!res.ok) {
        throw new Error(`API status ${res.status}`);
      }
      const data = await res.json();
      if (data.invitations) {
        setInvitations(data.invitations);
      }
    } catch (err) {
      console.warn("Unable to fetch invitations API, using existing data:", err);
    }
  };

  useEffect(() => {
    void fetchInvitations();
  }, []);

  const openInviteModal = (roleKey: string) => {
    setActiveModalRole(roleKey);
    setEmailInput("");
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const handleSendInvitation = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!emailInput || !emailInput.includes("@")) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    try {
      setSending(true);
      setErrorMessage(null);
      setSuccessMessage(null);

      const res = await fetch(`${API_BASE_URL}/api/invitations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: emailInput.trim(),
          inviteType: activeModalRole,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to send invitation.");
      }

      setSuccessMessage(
        data.emailResult?.sent
          ? `Invitation email sent via Mailcow SMTP to ${emailInput.trim()}!`
          : `Invitation created for ${emailInput.trim()} & stored in database. (Mailcow SMTP server link ready for production).`
      );

      setEmailInput("");
      setActiveModalRole(null);
      await fetchInvitations();
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Unable to send invitation."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="mb-2 inline-block rounded-full bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-300">
              Administrator Control
            </div>
            <h1 className="text-4xl font-bold md:text-6xl">Invitations</h1>
            <p className="mt-2 text-lg text-slate-300">
              Manage Account Salesperson, PSE, BSP and EAH invitations by role and status.
            </p>
          </div>

          <Link
            href="/admin/dashboard"
            className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-200 transition hover:border-slate-500"
          >
            Back to Admin Dashboard
          </Link>
        </div>

        {statusMessage && (
          <div className="mb-6 rounded-2xl border border-emerald-500/50 bg-emerald-500/10 p-4 text-sm text-emerald-200">
            {statusMessage}
          </div>
        )}

        {errorMessage && (
          <div className="mb-6 rounded-2xl border border-red-500/50 bg-red-500/10 p-4 text-sm text-red-200">
            {errorMessage}
          </div>
        )}

        {/* Invitation Trigger Buttons */}
        <div className="mb-8 grid gap-4 md:grid-cols-4">
          {INVITE_ROLES.map((role) => (
            <button
              key={role.key}
              type="button"
              onClick={() => openInviteModal(role.key)}
              className="rounded-2xl border border-blue-500/40 bg-blue-600/20 px-4 py-3.5 font-semibold text-blue-100 transition hover:bg-blue-600 hover:text-white"
            >
              {role.label}
            </button>
          ))}
        </div>

        {/* Modal Form for Sending Invitation */}
        {activeModalRole && (
          <div className="mb-8 rounded-3xl border border-slate-800 bg-slate-900 p-6 md:p-8">
            <div className="mb-4 flex items-center justify-between border-b border-slate-800 pb-3">
              <h2 className="text-xl font-bold text-white">
                Send Invitation as <span className="text-blue-400">{activeModalRole}</span>
              </h2>
              <button
                type="button"
                onClick={() => setActiveModalRole(null)}
                className="text-sm text-slate-400 hover:text-white"
              >
                Cancel
              </button>
            </div>

            <form onSubmit={handleSendInvitation} className="flex flex-col gap-4 md:flex-row md:items-end">
              <div className="flex-1">
                <label className="mb-1.5 block text-sm font-medium text-slate-300">
                  Recipient Email Address
                </label>
                <input
                  type="email"
                  required
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="e.g. user@domain.com"
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-blue-500"
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                className="rounded-xl border border-blue-500/50 bg-blue-600 px-6 py-3 font-semibold text-white transition hover:bg-blue-500 disabled:opacity-50"
              >
                {sending ? "Sending..." : `Send ${activeModalRole} Invite`}
              </button>
            </form>
          </div>
        )}

        {/* Stored Invitations Table */}
        <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900">
          <div className="hidden md:grid md:grid-cols-4 bg-slate-800 p-5 font-semibold text-slate-100">
            <div>Email Address</div>
            <div>Invite Type / Role</div>
            <div>Status</div>
            <div>Date Created</div>
          </div>

          {invitations.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              No invitations found. Click one of the buttons above to invite a participant.
            </div>
          ) : (
            invitations.map((invitation) => (
              <div
                key={invitation.id || invitation.email}
                className="grid gap-4 border-t border-slate-800 p-6 md:grid-cols-4 md:p-5"
              >
                <div className="font-medium text-white break-all">{invitation.email}</div>
                <div>
                  <span className="rounded-md bg-blue-500/20 px-2.5 py-1 text-xs font-semibold text-blue-300">
                    {invitation.inviteType}
                  </span>
                </div>
                <div>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      invitation.accepted
                        ? "bg-emerald-500/20 text-emerald-300"
                        : "bg-amber-500/20 text-amber-300"
                    }`}
                  >
                    {invitation.accepted ? "Accepted" : "Sent / Pending"}
                  </span>
                </div>
                <div className="text-sm text-slate-300">
                  {invitation.createdAt
                    ? new Date(invitation.createdAt).toLocaleDateString()
                    : "Recent"}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </main>
  );
}
